// PageFilterContext.js
import * as types from '@/generated'
import { FieldInput } from '@/generated'
import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react'
import { useFiltersByIdsLazyQuery } from '@/hooks/useFilters/useFilters.generated'
import { FilterFragment } from '@/ux/Filter/Filter.generated'
import { usePlatformOutletContext } from '@/ux/Platform/usePlatFormOutletContext'
import { buildFilterInputs, normalisePrimaryPageFilters, removeNullFilters } from './utils'
import { useFilterIds } from '../FilterIdProvider/FilterIdsProvider'
import { LoadingComponent } from '@/ui/Loading/LoadingComponent'
import { ErrorComponent } from '@/ui/ErrorComponent'

type PageFilterContextType = {
  updatePageFilters: (id: string, option: types.SelectOption | null | undefined) => void
  updatePrimaryPageFilters: (id: string, option: types.SelectOption | null | undefined) => void
  resetPageFilters: () => void
  selectClear: (id: string) => void
  multiSelect: (id: string, options: types.SelectOption[] | null | undefined) => void
  multiSelectAll: (id: string) => void
  multiClearAll: (id: string) => void
  pageFilters: FilterFragment[]
  primaryPageFilters: FilterFragment[]
  pageFilterInputs: FieldInput[] | null
}

export const PageFilterContext = createContext<PageFilterContextType | null>(null)

export const usePageFilters = () => {
  const context = useContext(PageFilterContext)
  if (!context) {
    throw new Error('usePageFilter must be used within a PageFilterContextProvider')
  }
  return context
}

export const PageFilterContextProvider2 = ({ children }: { children: ReactNode }) => {
  const { appId, appKey, projectId } = usePlatformOutletContext()
  const { filterIds, primaryFilterIds } = useFilterIds()
  // State for storing transformed filter data
  const [pageFilters, setPageFilters] = useState<FilterFragment[]>([])
  const [primaryPageFilters, setPrimaryPageFilters] = useState<FilterFragment[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const pageFilterInputs = useMemo(() => {
    const nonNullPageFilters = pageFilters?.filter((x): x is FilterFragment => x !== null) ?? []
    const nonNullPrimaryPageFilters = primaryPageFilters?.filter((x): x is FilterFragment => x !== null) ?? []
    return buildFilterInputs([...nonNullPageFilters, ...nonNullPrimaryPageFilters])
  }, [pageFilters, primaryPageFilters])

  // Get the query functions from your custom hooks
  const [getPageFilters, { loading: filtersLoading }] = useFiltersByIdsLazyQuery()

  const extraInputs = useMemo(
    () => [
      { name: 'appId', value: `${appId}` },
      { name: 'projectId', value: `${projectId}` },
    ],
    [appId, projectId]
  )

  // Main function to fetch and process all filters
  const fetchFilters = useCallback(async () => {
    try {
      setLoaded(false)
      setError(null)

      const filterPromises = []
      let regularFiltersResponse = null
      let primaryFiltersResponse = null

      // Only fetch regular filters if filterIds exist
      if (filterIds.length > 0) {
        filterPromises.push(
          getPageFilters({
            fetchPolicy: 'no-cache',
            variables: {
              appId: appKey,
              projectId,
              filterIds,
              extraInputs,
            },
          })
        )
      }

      // Only fetch primary filters if primaryFilterIds exist
      if (primaryFilterIds.length > 0) {
        filterPromises.push(
          getPageFilters({
            fetchPolicy: 'no-cache',
            variables: {
              appId: appKey,
              projectId,
              filterIds: primaryFilterIds,
              extraInputs,
            },
          })
        )
      }

      // If we have any filters to fetch, do it in parallel
      if (filterPromises.length > 0) {
        const responses = await Promise.all(filterPromises)
        if (filterIds.length > 0) {
          regularFiltersResponse = responses[0]
          if (primaryFilterIds.length > 0) {
            primaryFiltersResponse = responses[1]
          }
        } else if (primaryFilterIds.length > 0) {
          primaryFiltersResponse = responses[0]
        }
      }

      // Set regular filters
      setPageFilters(
        filterIds.length > 0
          ? removeNullFilters(regularFiltersResponse?.data?.app?.filtersByIds as FilterFragment[])
          : []
      )

      // Set primary filters
      setPrimaryPageFilters((current) => {
        if (primaryFilterIds.length === 0) return []

        const normalisedPrimaryFilters = normalisePrimaryPageFilters(
          current,
          primaryFiltersResponse?.data?.app?.filtersByIds as FilterFragment[],
          primaryFilterIds,
          appKey
        )
        return removeNullFilters(normalisedPrimaryFilters)
      })

      setLoaded(true)
    } catch (err) {
      setError(err)
      console.error('Error fetching filters:', err)
    }
  }, [appKey, extraInputs, filterIds, getPageFilters, primaryFilterIds, projectId])

  // Filter manipulation handlers
  const handlers = useMemo(
    () => ({
      // Add more handlers as needed
      updatePageFilters: (id: string, option: types.SelectOption | null | undefined, extra?: unknown) => {
        console.debug(`updatePageFilters ${id} ${JSON.stringify(option)} ${JSON.stringify(extra)}`)
        if (!option) {
          return
        }
        setPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id !== id) {
              return field
            }
            if (field.__typename === 'SelectField') {
              return {
                ...field,
                localSelection: option,
              }
            }
            if (field.__typename === 'MultiSelectField') {
              const currentSelections = field.localSelections ?? field.selections ?? []
              const filtered = currentSelections.filter((it) => it.value !== option?.value)
              const object = {
                ...field,
                localSelections:
                  filtered.length < currentSelections.length
                    ? // option was filtered out
                      filtered
                    : // option must be added
                      [...filtered, option],
              }
              return object
            }
            // Extend othe filter types here
            return field
          })

          return updated
        })
      },
      updatePrimaryPageFilters: (id: string, option: types.SelectOption | null | undefined, extra?: unknown) => {
        console.debug(`updatePrimaryPageFilters ${id} ${JSON.stringify(option)} ${JSON.stringify(extra)}`)
        if (!option) {
          return
        }
        setPrimaryPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id !== id) {
              return field
            }
            if (field.__typename === 'SelectField') {
              return {
                ...field,
                localSelection: option,
              }
            }
            if (field.__typename === 'MultiSelectField') {
              const currentSelections = field.localSelections ?? field.selections ?? []
              const filtered = currentSelections.filter((it) => it.value !== option?.value)
              const object = {
                ...field,
                localSelections:
                  filtered.length < currentSelections.length
                    ? // option was filtered out
                      filtered
                    : // option must be added
                      [...filtered, option],
              }
              return object
            }
            // Extend othe filter types here
            return field
          })

          return updated
        })
      },
      selectClear: (id: string) => {
        setPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'SelectField') {
              return {
                ...field,
                localSelection: undefined,
              }
            }
            return field
          })

          return updated
        })
        setPrimaryPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'SelectField') {
              return {
                ...field,
                localSelection: undefined,
              }
            }
            return field
          })

          return updated
        })
      },
      multiSelect: (id: string, options: types.SelectOption[] | null | undefined, _extra?: unknown) => {
        setPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'MultiSelectField') {
              return {
                ...field,
                localSelections: options,
              }
            }
            return field
          })

          return updated
        })
        setPrimaryPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'MultiSelectField') {
              return {
                ...field,
                localSelections: options,
              }
            }
            return field
          })

          return updated
        })
      },
      multiSelectAll: (id: string) => {
        setPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'MultiSelectField') {
              return {
                ...field,
                localSelections: field.options,
              }
            }
            return field
          })

          return updated
        })
        setPrimaryPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'MultiSelectField') {
              return {
                ...field,
                localSelections: field.options,
              }
            }
            return field
          })

          return updated
        })
      },
      multiClearAll: (id: string) => {
        setPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'MultiSelectField') {
              return {
                ...field,
                localSelections: [],
              }
            }
            return field
          })

          return updated
        })
        setPrimaryPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.id === id && field.__typename === 'MultiSelectField') {
              return {
                ...field,
                localSelections: [],
              }
            }
            return field
          })

          return updated
        })
      },
      resetPageFilters: () => {
        setPageFilters((prev) => {
          const updated = prev?.map((field) => {
            if (field.__typename === 'SelectField') {
              return {
                ...field,
                localSelection: null,
              }
            }
            if (field.__typename === 'MultiSelectField') {
              const object = {
                ...field,
                localSelections: null,
              }
              return object
            }
            return field
          })

          return updated
        })
      },
    }),
    []
  )

  // Load filters on mount
  useEffect(() => {
    fetchFilters()
    return () => setLoaded(false)
  }, [fetchFilters])

  const contextValue = useMemo(
    () => ({
      pageFilters,
      primaryPageFilters,
      pageFilterInputs,
      ...handlers,
    }),
    [handlers, pageFilterInputs, pageFilters, primaryPageFilters]
  )

  if (!loaded) {
    return <LoadingComponent />
  }

  if (error) {
    return <ErrorComponent />
  }

  return <PageFilterContext.Provider value={contextValue}>{children}</PageFilterContext.Provider>
}
